import { Banner } from "@/components/homepage/Banner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useHandleError } from "@/hooks/useHandleError";
import { useRedeemPointsLink } from "@/hooks/useRedeemPointsLink";
import { useWebUserRoundEntryUnits } from "@/hooks/useWebUserRoundEntryUnits";
import { useAuthStore } from "@/store/auth.store";
import { useRealtimeStore } from "@/store/realtime.store";
import { cn } from "@roman/shared/utils";
import { useLocation, useNavigate } from "react-router-dom";
import { BeginnerIcon } from "../Icons/beginner-icon";
import { HistoryIcon } from "../Icons/history-icon";
import { HomeIcon } from "../Icons/home-icon";
import { ThumbaCircleIcon } from "../Icons/thumba-circle-icon";
import { TrophyIcon } from "../Icons/trophy-icon";
import { ComingSoonBadge } from "./CommingSoonBadge";
import { MyContestBadge } from "./MyContestBadge";

export enum NavigationItemId {
  Home = 0,
  Upcoming = 1,
  MyContest = 2,
  PointAcquisition = 3,
  AboutSamba = 4,
}

export type MenuType = {
  id: NavigationItemId;
  name: string;
  bottomName: string;
  link: string;
  icon: (className?: string) => JSX.Element;
  isComingSoon?: boolean;
  isRequiredLogin: boolean;
  children: {
    id: string;
    name: string;
    link: string;
  }[];
};

export const menuLists: MenuType[] = [
  {
    id: NavigationItemId.Home,
    name: "ホーム",
    bottomName: "ホーム",
    link: "/",
    isRequiredLogin: false,
    icon: (className?: string) => <HomeIcon className={className} />,
    children: [],
  },
  {
    id: NavigationItemId.Upcoming,
    name: "開催予定の試合",
    bottomName: "開催予定の試合",
    link: "/my-entry?tab=schedule",
    isRequiredLogin: true,
    icon: (className?: string) => <HistoryIcon className={className} />,
    children: [],
  },
  {
    id: NavigationItemId.MyContest,
    name: "マイエントリー",
    bottomName: "マイエントリー",
    link: "/my-entry?tab=live",
    isRequiredLogin: true,
    icon: (className?: string) => <TrophyIcon className={className} />,
    children: [],
  },
  {
    id: NavigationItemId.PointAcquisition,
    name: "無料でポイントを獲得",
    bottomName: "無料でポイント",
    link: "/redeem-points",
    isRequiredLogin: false,
    icon: (className?: string) => <ThumbaCircleIcon className={className} />,
    children: [],
  },
  {
    id: NavigationItemId.AboutSamba,
    name: "サンバについて",
    bottomName: "",
    link: "/top",
    isRequiredLogin: false,
    icon: (className?: string) => <BeginnerIcon className={className} />,
    children: [],
  },
];

export const Menu = () => {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((state) => state.authState === "authenticated");
  const setHasLiveRounds = useRealtimeStore((state) => state.setHasLiveRounds);

  const location = useLocation();

  const currentPath = `${location.pathname}${location.search}`;

  // Menu is always show on the screen, so we don't need to fetch in BottomNavigationBar.
  useWebUserRoundEntryUnits({
    status: "live",
    lang: "ja",
    swrOptions: {
      onSuccess: (data) => {
        setHasLiveRounds(data.userRoundEntryUnits.length > 0);
      },
      onError: useHandleError("ユーザーのラウンドエントリーユニットを取得できませんでした"),
    },
  });

  const handleClickLink = (event: React.MouseEvent, menu: MenuType) => {
    event.preventDefault();

    if (menu.isComingSoon) {
      return;
    }

    if (menu.id === NavigationItemId.AboutSamba) {
      navigate("/top", { state: { isViewedTop: true } });
    } else if (isAuthenticated || !menu.isRequiredLogin) {
      navigate(menu.link);
    } else {
      navigate("/login");
    }
  };

  const { onClick: onClickRedeemPointsLink } = useRedeemPointsLink({
    spram1: "GN",
  });

  return (
    <div className="sticky top-[72px] ">
      <Accordion className="px-5 xl:px-13 h-full" type="single" collapsible>
        {menuLists.map((menu) => (
          <AccordionItem
            key={menu.id}
            value={`item-${menu.id}`}
            className="bg-transparent border-none focus:ring-0 px-5"
          >
            <AccordionTrigger
              iconClassName="text-secondary font-bold"
              className={cn("flex space-x-2 hover:no-underline", !menu.children.length && "pointer-events-none")}
            >
              <div
                onClick={(event: React.MouseEvent) => handleClickLink(event, menu)}
                className={cn(
                  "w-full select-none",
                  menu.isComingSoon ? "opacity-70  cursor-default" : "pointer-events-auto",
                )}
              >
                <div className="flex items-center space-x-2">
                  {menu.icon(cn("min-w-6 min-h-6", currentPath === menu.link ? "text-primary" : "text-gray"))}

                  <div className="flex flex-col justify-center space-x-2">
                    {menu.isComingSoon && (
                      <ComingSoonBadge
                        className="relative transform-none translate-x-0 left-0 -top-0 w-fit"
                        subClassName="px-[6px]"
                      />
                    )}

                    <div className="flex items-center space-x-2">
                      <span className="font-bold text-dark-gray text-[16px] leading-[20px]">{menu.name}</span>
                      {menu.id === NavigationItemId.MyContest && (
                        <div className="relative">
                          <MyContestBadge
                            className="h-[20px] w-[58px] relative top-0"
                            subClassName="px-[11px]"
                            iconClassName="w-[10px] h-[10px]"
                            textClassName="text-[11px] font-semibold"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </AccordionTrigger>

            <AccordionContent>
              {menu.children.map((child) => (
                <a
                  key={child.id}
                  href={child.link}
                  className="block px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 text-disabled font-semibold"
                >
                  {child.name}
                </a>
              ))}
            </AccordionContent>
          </AccordionItem>
        ))}
        <div className="mt-4 px-3">
          <Banner
            imageSrc="/assets/banners/安心安全バナー.png"
            imageAlt="安心安全バナー"
            onClick={() => window.open("https://thumba.jp/media/216", "_blank")}
          />
          <Banner
            imageSrc="/assets/banners/pc_激熱大会を見逃すな_252 x 78.png"
            imageAlt="pc_激熱大会を見逃すな_252 x 78"
            onClick={() => window.open("https://lin.ee/4f3IfmZ", "_blank")}
          />
          <Banner
            imageSrc="/assets/banners/無料エントリー獲得バナー.png"
            imageAlt="無料エントリー獲得バナー"
            onClick={onClickRedeemPointsLink}
          />
        </div>
      </Accordion>
    </div>
  );
};
